import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const navLinks = [
  { label: "How It Works", href: "/#how-it-works", isHash: true },
  { label: "Demo", href: "/#demo", isHash: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (link) => {
    if (link.isRoute) {
      navigate(link.href);
    } else if (link.isHash) {
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          document.querySelector(link.href.replace("/#", "#"))?.scrollIntoView({ behavior: "smooth" });
        }, 300);
      } else {
        document.querySelector(link.href.replace("/#", "#"))?.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5 shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <a href="/" className="flex items-center" data-testid="logo">
          <span className="text-white text-xl md:text-2xl font-extrabold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Appoin<span className="text-white">Text</span>{" "}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">Ai</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link)}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-zinc-400 hover:text-white text-sm font-medium transition-colors duration-200 bg-transparent border-none cursor-pointer"
            >
              {link.label}
            </button>
          ))}
          <Button
            data-testid="nav-cta-button"
            onClick={() => window.location.href = "/apply.html"}
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 shadow-[0_0_20px_rgba(59,130,246,0.3)]"
          >
            Book a Call
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden backdrop-blur-xl bg-[#0A0A0A]/95 border-b border-white/5 px-6 pb-6">
          <div className="flex flex-col gap-4 pt-4">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => { handleNavClick(link); setMobileOpen(false); }}
                className="text-zinc-300 hover:text-white text-base font-medium transition-colors text-left bg-transparent border-none cursor-pointer"
              >
                {link.label}
              </button>
            ))}
            <Button
              data-testid="mobile-cta-button"
              onClick={() => window.location.href = "/apply.html"}
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6 py-3 text-sm font-semibold mt-2"
            >
              Book a Call
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
